<template>
  <b-modal
    v-model="showModal"
    title="Cash Register Report"
    size="xl"
    centered
    no-close-on-backdrop
    @shown="onModalShown"
  >
    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div
        v-if="typeof loadingError === 'object'"
        class="alert-body"
      >
        <p
          v-for="(message, index) in loadingError"
          :key="index"
        >
          {{ message }}
        </p>
      </div>
      <p
        v-else
        class="alert-body"
      >
        {{ loadingError }}
      </p>
    </b-alert>

    <div class="mb-2 d-flex justify-content-between align-items-center">
      <b-form-group
        label="Select Date"
        class="mb-0"
      >
        <flat-picker
          v-model="selectedDate"
          placeholder="Select Date"
          @input="fetchAllData"
        />
      </b-form-group>

      <b-button
        variant="outline-primary"
        :disabled="loading || printing"
        @click="handlePrint"
      >
        <b-spinner
          v-if="printing"
          small
          class="mr-1"
        />
        <feather-icon
          v-else
          icon="PrinterIcon"
          class="mr-1"
        />
        Print
      </b-button>
    </div>

    <div
      v-if="loading"
      class="text-center my-5"
    >
      <b-spinner
        label="Loading..."
        variant="primary"
      />
    </div>

    <div v-else-if="!loadingError">
      <div class="table-responsive">
        <table class="table table-bordered table-sm compact-table">
          <thead>
            <tr>
              <th
                rowspan="2"
                class="align-middle text-center"
              >
                Time
              </th>
              <th
                colspan="4"
                class="text-center bg-light"
              >
                Bills
              </th>
              <th
                colspan="3"
                class="text-center bg-info text-white"
              >
                Expense
              </th>
              <th
                colspan="3"
                class="text-center bg-warning"
              >
                Loan
              </th>
            </tr>
            <tr>
              <!-- Bills subcolumns -->
              <th class="bg-light">
                Amount
              </th>
              <th class="bg-light">
                Payment Mode
              </th>
              <th class="bg-light fixed-width-column">
                Party/Remarks
              </th>
              <th class="bg-light">
                Inv No
              </th>
              <!-- Expense subcolumns -->
              <th class="bg-info text-white">
                Amount
              </th>
              <th class="bg-info text-white">
                Expense Type
              </th>
              <th class="bg-info text-white fixed-width-column">
                Remark
              </th>
              <!-- Loan subcolumns -->
              <th class="bg-warning">
                Amount
              </th>
              <th class="bg-warning">
                Borrower
              </th>
              <th class="bg-warning">
                Type
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in combinedData"
              :key="index"
            >
              <td class="text-center">
                <strong>{{ item.time }}</strong>
              </td>
              <!-- Bills columns -->
              <td>{{ item.billAmount ? `₹${item.billAmount}` : '' }}</td>
              <td>
                <div>{{ item.billPaymentMode || '' }}</div>

                <!-- Show remark below for specific modes -->
                <small
                  v-if="['mr_handover', 'loan'].includes(item.billPaymentMode) && item.billPartyOrRemark"
                  class="text-primary d-block"
                  style="font-size: 10px;"
                >
                  {{ item.billRemark }}
                </small>
              </td>
              <td class="fixed-width-column">
                {{ item.billPartyOrRemark || '' }}
              </td>
              <td>{{ item.billInvNo || '' }}</td>
              <!-- Expense columns -->
              <td>{{ item.expenseAmount ? `₹${item.expenseAmount}` : '' }}</td>
              <td>{{ item.expenseType || '' }}</td>
              <td class="fixed-width-column">
                {{ item.expenseRemark || '' }}
              </td>
              <!-- Loan columns -->
              <td>{{ item.loanAmount ? `₹${item.loanAmount}` : '' }}</td>
              <td>{{ item.loanBorrower || '' }}</td>
              <td>
                <b-badge
                  v-if="item.loanType"
                  :variant="item.loanType === 'Issued' ? 'warning' : 'success'"
                  class="w-100"
                >
                  {{ item.loanType }}
                </b-badge>
              </td>
            </tr>
          </tbody>
          <tfoot class="table-footer-highlight">
            <tr>
              <td class="text-right font-weight-bold">
                Total:
              </td>
              <td class="font-weight-bold">
                ₹{{ totals.bills }}
              </td>
              <td colspan="3" />
              <td class="font-weight-bold">
                ₹{{ totals.expense }}
              </td>
              <td colspan="2" />
              <td class="font-weight-bold">
                ₹{{ totals.loan }}
              </td>
              <td colspan="2" />
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Cash Summary Section -->
      <div
        v-if="cashData"
        class="cash-summary-section"
      >
        <div class="cash-summary-container">
          <div class="cash-summary-header">
            Cash Summary
          </div>

          <div class="cash-summary-body">
            <!-- Total Cash -->
            <div class="cash-total-row">
              <span class="cash-label">Total Cash:</span>
              <span
                class="cash-value-large"
                :class="cashData.total_cash >= 0 ? 'text-success' : 'text-danger'"
              >
                ₹{{ formatAmount(cashData.total_cash) }}
              </span>
            </div>

            <!-- Detail Cards Row -->
            <div class="cash-details-row">
              <!-- Opening Balance -->
              <div class="cash-detail-box">
                Opening: ₹{{ formatAmount(cashData.opening_balance) }}
              </div>

              <!-- Payment Received Section -->
              <div class="cash-detail-column">
                <div class="cash-detail-box section-header-positive">
                  Payment Received: <span class="text-success">₹{{ formatAmount(totalPaymentReceived) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_cash"
                  class="cash-detail-sub-box"
                >
                  <span>Cash:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_by_cash) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_upi"
                  class="cash-detail-sub-box"
                >
                  <span>UPI:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_by_upi) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_cheque"
                  class="cash-detail-sub-box"
                >
                  <span>Cheque:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_by_cheque) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_rgts"
                  class="cash-detail-sub-box"
                >
                  <span>RTGS:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_by_rgts) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_credit_note"
                  class="cash-detail-sub-box"
                >
                  <span>Cr. Note:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_by_credit_note) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_by_challan"
                  class="cash-detail-sub-box"
                >
                  <span>Challan:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_by_challan) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_mr_handover"
                  class="cash-detail-sub-box"
                >
                  <span>MR Handover :</span>
                  <span>₹{{ formatAmount(cashData.payment_received_as_mr_handover) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_loan"
                  class="cash-detail-sub-box"
                >
                  <span>As Loan:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_as_loan) }}</span>
                </div>
                <div
                  v-if="cashData.payment_received_as_loan"
                  class="cash-detail-sub-box"
                >
                  <span>Rec By Acc. Dept.:</span>
                  <span>₹{{ formatAmount(cashData.payment_received_as_rec_by_acc_dept) }}</span>
                </div>
              </div>

              <!-- Loan Section -->
              <div class="cash-detail-column">
                <div class="cash-detail-box section-header-loan">
                  Loan (Net): <span :class="netLoan >= 0 ? 'text-success' : 'text-danger'">₹{{ formatAmount(netLoan) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_cash"
                  class="cash-detail-sub-box"
                >
                  <span>Rcvd (Cash):</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_cash) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_upi"
                  class="cash-detail-sub-box"
                >
                  <span>Rcvd (UPI):</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_upi) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_cheque"
                  class="cash-detail-sub-box"
                >
                  <span>Rcvd (Cheque):</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_cheque) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_rgts"
                  class="cash-detail-sub-box"
                >
                  <span>Rcvd (RTGS):</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_rgts) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_credit_note"
                  class="cash-detail-sub-box"
                >
                  <span>Rcvd (Cr. Note):</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_credit_note) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_challan"
                  class="cash-detail-sub-box"
                >
                  <span>Rcvd (Challan):</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_challan) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_transferred_to_salary_advance"
                  class="cash-detail-sub-box"
                >
                  <span>To Sal. Adv.:</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_transferred_to_salary_advance) }}</span>
                </div>
                <div
                  v-if="cashData.loan_received_by_transferred_to_medicine_advance"
                  class="cash-detail-sub-box"
                >
                  <span>To Med. Adv.:</span>
                  <span class="text-success">₹{{ formatAmount(cashData.loan_received_by_transferred_to_medicine_advance) }}</span>
                </div>
                <div
                  v-if="cashData.loans_taken"
                  class="cash-detail-sub-box"
                >
                  <span>Taken:</span>
                  <span class="text-danger">₹{{ formatAmount(cashData.loans_taken) }}</span>
                </div>
              </div>

              <!-- Expenses -->
              <div class="cash-detail-box section-header-negative">
                Expenses: <span class="text-danger">₹{{ formatAmount(cashData.expenses) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button
          variant="secondary"
          @click="showModal=false"
        >
          Close
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BAlert, BButton, BFormGroup, BBadge,
} from 'bootstrap-vue'
import axios from 'axios'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import getParsedQueryString from '@/utils/headerQueryParser'
import dateMixin from '@/mixins/dateMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BModal,
    BSpinner,
    BAlert,
    BButton,
    BFormGroup,
    BBadge,
    FlatPicker,
  },
  mixins: [dateMixin],
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      printing: false,
      loadingError: null,
      selectedDate: '',
      receivedAmounts: [],
      loans: [],
      expenses: [],
      cashData: null,
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      },
    },
    combinedData() {
      const dataArray = []

      // Process received amounts (bills)
      this.receivedAmounts.forEach(item => {
        dataArray.push({
          timestamp: new Date(item.created_at).getTime(),
          time: this.formatTime(item.created_at),
          billAmount: item.received_amount,
          billPaymentMode: item.payment_mode,
          billPartyOrRemark: item.party_name || item.remarks || '',
          billRemark: item.remarks || '',
          billInvNo: item.invoice_number || '',
        })
      })

      // Process expenses
      this.expenses.forEach(item => {
        dataArray.push({
          timestamp: new Date(item.created_at).getTime(),
          time: this.formatTime(item.created_at),
          expenseAmount: item.amount,
          expenseType: item.expense_category_name || 'N/A',
          expenseRemark: item.remarks || '',
        })
      })

      // Process loans (both issued and repayments)
      this.loans.forEach(item => {
        // Loan issued
        if (item.taken_at) {
          dataArray.push({
            timestamp: new Date(item.taken_at).getTime(),
            time: this.formatTime(item.taken_at),
            loanAmount: item.amount,
            loanBorrower: item.borrower_name,
            loanType: 'Issued',
          })
        }

        // Loan repaid
        if (item.repayed_at) {
          dataArray.push({
            timestamp: new Date(item.repayed_at).getTime(),
            time: this.formatTime(item.repayed_at),
            loanAmount: item.amount,
            loanBorrower: item.borrower_name,
            loanType: 'Repaid',
          })
        }
      })

      // Sort by timestamp
      return dataArray.sort((a, b) => a.timestamp - b.timestamp)
    },
    totals() {
      const totals = {
        bills: 0,
        expense: 0,
        loan: 0,
      }

      this.combinedData.forEach(row => {
        if (row.billAmount) totals.bills += parseFloat(row.billAmount)
        if (row.expenseAmount) totals.expense += parseFloat(row.expenseAmount)
        if (row.loanAmount) {
          if (row.loanType === 'Issued') {
            totals.loan += parseFloat(row.loanAmount)
          } else {
            totals.loan -= parseFloat(row.loanAmount)
          }
        }
      })

      return {
        bills: totals.bills.toFixed(2),
        expense: totals.expense.toFixed(2),
        loan: totals.loan.toFixed(2),
      }
    },
    totalPaymentReceived() {
      if (!this.cashData) return 0
      return (
        (this.cashData.payment_received_by_cash || 0)
        + (this.cashData.payment_received_by_upi || 0)
        + (this.cashData.payment_received_by_cheque || 0)
        + (this.cashData.payment_received_by_rgts || 0)
        + (this.cashData.payment_received_by_credit_note || 0)
        + (this.cashData.payment_received_by_challan || 0)
        + (this.cashData.payment_received_as_mr_handover || 0)
        + (this.cashData.payment_received_as_loan || 0)
        + (this.cashData.payment_received_as_rec_by_acc_dept || 0)
      )
    },
    totalLoanReceived() {
      if (!this.cashData) return 0
      return (
        (this.cashData.loan_received_by_cash || 0)
        + (this.cashData.loan_received_by_upi || 0)
        + (this.cashData.loan_received_by_cheque || 0)
        + (this.cashData.loan_received_by_rgts || 0)
        + (this.cashData.loan_received_by_credit_note || 0)
        + (this.cashData.loan_received_by_challan || 0)
        + (this.cashData.loan_received_by_transferred_to_salary_advance || 0)
      )
    },
    netLoan() {
      if (!this.cashData) return 0
      return this.totalLoanReceived - (this.cashData.loans_taken || 0)
    },
  },
  methods: {
    onModalShown() {
      // Set default date to today
      const today = this.formatedDate(new Date(), 'YYYY-MM-DD')
      this.selectedDate = today
      this.fetchAllData()
    },
    formatTime(datetime) {
      const date = new Date(datetime)
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    async fetchAllData() {
      if (!this.selectedDate) return

      this.loading = true
      this.loadingError = null

      try {
        const params = {
          page: 1,
          per_page: 500,
          start_date: this.selectedDate,
          end_date: this.selectedDate,
        }

        // Prepare loan params for both queries
        const loanParamsCreated = { ...params }
        const loanParamsRepayment = {
          ...params,
          repayment_start_date: this.selectedDate,
          repayment_end_date: this.selectedDate,
        }
        delete loanParamsRepayment.start_date
        delete loanParamsRepayment.end_date

        // Fetch all data concurrently
        const [receivedRes, loansCreatedRes, loansRepaymentRes, expensesRes, cashRes] = await Promise.all([
          axios.get(`/operations/received_amount/?${getParsedQueryString(params)}`),
          axios.get(`/operations/loans/?${getParsedQueryString(loanParamsCreated)}`),
          axios.get(`/operations/loans/?${getParsedQueryString(loanParamsRepayment)}`),
          axios.get(`/operations/expenses/?${getParsedQueryString(params)}`),
          axios.get(`/operations/todays_cash?${getParsedQueryString({ date: this.selectedDate })}`),
        ])

        // Merge loans and remove duplicates by id
        const allLoans = [...loansCreatedRes.data.results, ...loansRepaymentRes.data.results]
        const uniqueLoansMap = new Map()
        allLoans.forEach(loan => {
          if (!uniqueLoansMap.has(loan.id)) {
            uniqueLoansMap.set(loan.id, loan)
          }
        })

        this.receivedAmounts = receivedRes.data.results
        this.loans = Array.from(uniqueLoansMap.values())
        this.expenses = expensesRes.data.results
        this.cashData = cashRes.data
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load data. Please try again.'
        this.receivedAmounts = []
        this.loans = []
        this.expenses = []
        this.cashData = null
      } finally {
        this.loading = false
      }
    },
    async handlePrint() {
      try {
        this.printing = true
        await this.printCashRegisterReport()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.message || 'Failed to print!',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.printing = false
      }
    },
    printCashRegisterReport() {
      return new Promise((resolve, reject) => {
        try {
          const formattedDate = this.formatedDate(this.selectedDate, 'DD-MM-YYYY')

          // Build summary section
          const summaryHTML = `
  <div style="margin-bottom: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px;">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
      <h2 style="margin: 0; font-size: 20px;">Cash Register Report</h2>
      <div style="font-size: 14px; font-weight: bold;">Date: ${formattedDate}</div>
    </div>
    <div style="display: flex; gap: 30px; flex-wrap: wrap;">
      <div><strong>Total Bills:</strong> ₹${this.totals.bills}</div>
      <div><strong>Total Expense:</strong> ₹${this.totals.expense}</div>
      <div><strong>Total Loan:</strong> ₹${this.totals.loan}</div>
    </div>
  </div>
`

          // Build table HTML
          let tableHTML = '<table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">'

          // Add header rows
          tableHTML += '<thead>'

          // First header row
          tableHTML += '<tr>'
          tableHTML += '<th rowspan="2" style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: center;">Time</th>'
          tableHTML += '<th colspan="4" style="background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: center;">Bills</th>'
          tableHTML += '<th colspan="3" style="background-color: #d1ecf1 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: center;">Expense</th>'
          tableHTML += '<th colspan="3" style="background-color: #fff3cd !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 12px; border: 1px solid #000; padding: 8px; text-align: center;">Loan</th>'
          tableHTML += '</tr>'

          // Second header row (subcolumns)
          tableHTML += '<tr>'
          // Bills subcolumns
          tableHTML += '<th style="background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Amount</th>'
          tableHTML += '<th style="background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Payment Mode</th>'
          tableHTML += '<th style="background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center; max-width: 150px; width: 150px;">Party/Remarks</th>'
          tableHTML += '<th style="background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Inv No</th>'
          // Expense subcolumns
          tableHTML += '<th style="background-color: #d1ecf1 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Amount</th>'
          tableHTML += '<th style="background-color: #d1ecf1 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Expense Type</th>'
          tableHTML += '<th style="background-color: #d1ecf1 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center; max-width: 150px; width: 150px;">Remark</th>'
          // Loan subcolumns
          tableHTML += '<th style="background-color: #fff3cd !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Amount</th>'
          tableHTML += '<th style="background-color: #fff3cd !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Borrower</th>'
          tableHTML += '<th style="background-color: #fff3cd !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: center;">Type</th>'
          tableHTML += '</tr>'
          tableHTML += '</thead>'

          // Add data rows
          tableHTML += '<tbody>'
          this.combinedData.forEach((item, index) => {
            const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f8f8'
            tableHTML += '<tr>'

            // Time
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: center; font-weight: bold;">${item.time}</td>`

            // Bills columns
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: right;">${item.billAmount ? `₹${item.billAmount}` : ''}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: left;">${item.billPaymentMode || ''}
${
  ['mr_handover', 'loan'].includes(item.billPaymentMode) && item.billRemark
    ? `<div style="font-size: 9px; color: #6c757d; margin-top: 2px;">${item.billRemark}</div>`
    : ''
}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: left; max-width: 150px; width: 150px; word-wrap: break-word; white-space: normal;">${item.billPartyOrRemark || ''}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: left;">${item.billInvNo || ''}</td>`

            // Expense columns
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: right;">${item.expenseAmount ? `₹${item.expenseAmount}` : ''}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: left;">${item.expenseType || ''}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: left; max-width: 150px; width: 150px; word-wrap: break-word; white-space: normal;">${item.expenseRemark || ''}</td>`

            // Loan columns
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: right;">${item.loanAmount ? `₹${item.loanAmount}` : ''}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: left;">${item.loanBorrower || ''}</td>`
            tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 5px; text-align: center;">${item.loanType || ''}</td>`

            tableHTML += '</tr>'
          })
          tableHTML += '</tbody>'

          // Add footer with totals
          tableHTML += '<tfoot>'
          tableHTML += '<tr>'
          tableHTML += '<td style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: right; font-weight: bold;">Total:</td>'
          tableHTML += `<td style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: right; font-weight: bold;">₹${this.totals.bills}</td>`
          tableHTML += '<td colspan="3" style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; border: 1px solid #000;"></td>'
          tableHTML += `<td style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: right; font-weight: bold;">₹${this.totals.expense}</td>`
          tableHTML += '<td colspan="2" style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; border: 1px solid #000;"></td>'
          tableHTML += `<td style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 11px; border: 1px solid #000; padding: 6px; text-align: right; font-weight: bold;">₹${this.totals.loan}</td>`
          tableHTML += '<td colspan="2" style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; border: 1px solid #000;"></td>'
          tableHTML += '</tr>'
          tableHTML += '</tfoot>'

          tableHTML += '</table>'

          // Build Cash Summary HTML
          let cashSummaryHTML = ''
          if (this.cashData) {
            cashSummaryHTML = `
  <div style="margin-top: 10px; page-break-before: avoid;">
    <div style="background-color: #007bff; color: white; padding: 4px 8px; border-radius: 4px 4px 0 0;">
      <h3 style="margin: 0; font-size: 11px; font-weight: 600;">Cash Summary</h3>
    </div>
    <div style="border: 1px solid #dee2e6; padding: 6px; border-radius: 0 0 4px 4px;">
      <!-- Total Cash -->
      <div style="background-color: #f8f9fa; padding: 4px 6px; margin-bottom: 6px; border: 2px solid #dee2e6; border-radius: 4px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="font-size: 10px; font-weight: bold;">Total Cash in Hand:</span>
          <span style="font-size: 10px; font-weight: bold; color: ${this.cashData.total_cash >= 0 ? '#28a745' : '#dc3545'};">₹${this.formatAmount(this.cashData.total_cash)}</span>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px;">
        <!-- Opening Balance -->
        <div style="border: 1px solid #dee2e6; padding: 2px 4px; border-radius: 4px;display: flex;justify-content: space-between;">
          <div style="font-size: 10px; color: #6c757d; margin-bottom: 1px; font-weight: 600;">Opening Balance</div>
          <div style="font-size: 10px; font-weight: bold;">₹${this.formatAmount(this.cashData.opening_balance)}</div>
        </div>

        <!-- Payment Received -->
        <div style="border: 1px solid #dee2e6; padding: 2px 4px; border-radius: 4px;">
          <div style="display: flex;justify-content: space-between;">
          <div style="font-size: 10px; color: #6c757d; margin-bottom: 1px; font-weight: bold;">Payment Received</div>
          <div style="font-size: 10px; font-weight: bold; color: #28a745; margin-bottom: 2px;">₹${this.formatAmount(this.totalPaymentReceived)}</div>
          </div>
          ${this.cashData.payment_received_by_cash ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Cash:</span><span>₹${this.formatAmount(this.cashData.payment_received_by_cash)}</span></div>` : ''}
          ${this.cashData.payment_received_by_upi ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>UPI:</span><span>₹${this.formatAmount(this.cashData.payment_received_by_upi)}</span></div>` : ''}
          ${this.cashData.payment_received_by_cheque ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Cheque:</span><span>₹${this.formatAmount(this.cashData.payment_received_by_cheque)}</span></div>` : ''}
          ${this.cashData.payment_received_by_rgts ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>RTGS:</span><span>₹${this.formatAmount(this.cashData.payment_received_by_rgts)}</span></div>` : ''}
          ${this.cashData.payment_received_by_credit_note ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Cr. Note:</span><span>₹${this.formatAmount(this.cashData.payment_received_by_credit_note)}</span></div>` : ''}
          ${this.cashData.payment_received_by_challan ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Challan:</span><span>₹${this.formatAmount(this.cashData.payment_received_by_challan)}</span></div>` : ''}
          ${this.cashData.payment_received_as_mr_handover ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Mr Handover:</span><span>₹${this.formatAmount(this.cashData.payment_received_as_mr_handover)}</span></div>` : ''}
          ${this.cashData.payment_received_as_loan ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>As Loan:</span><span>₹${this.formatAmount(this.cashData.payment_received_as_loan)}</span></div>` : ''}
          ${this.cashData.payment_received_as_rec_by_acc_dept ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rec By Acc. Dept.:</span><span>₹${this.formatAmount(this.cashData.payment_received_as_rec_by_acc_dept)}</span></div>` : ''}
          </div>

        <!-- Loan -->
        <div style="border: 1px solid #dee2e6; padding: 2px 4px; border-radius: 4px;">
          <div style="display: flex;justify-content: space-between;">
          <div style="font-size: 10px; color: #6c757d; margin-bottom: 1px; font-weight: bold;">Loan (Net)</div>
          <div style="font-size: 10px; font-weight: bold; color: ${this.netLoan >= 0 ? '#28a745' : '#dc3545'}; margin-bottom: 2px;">₹${this.formatAmount(this.netLoan)}</div>
          </div>
          ${this.cashData.loan_received_by_cash ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rcvd (Cash):</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_cash)}</span></div>` : ''}
          ${this.cashData.loan_received_by_upi ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rcvd (UPI):</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_upi)}</span></div>` : ''}
          ${this.cashData.loan_received_by_cheque ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rcvd (Cheque):</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_cheque)}</span></div>` : ''}
          ${this.cashData.loan_received_by_rgts ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rcvd (RTGS):</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_rgts)}</span></div>` : ''}
          ${this.cashData.loan_received_by_credit_note ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rcvd (Cr. Note):</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_credit_note)}</span></div>` : ''}
          ${this.cashData.loan_received_by_challan ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Rcvd (Challan):</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_challan)}</span></div>` : ''}
          ${this.cashData.loan_received_by_transferred_to_salary_advance ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>To Sal. Adv.:</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_transferred_to_salary_advance)}</span></div>` : ''}
          ${this.cashData.loan_received_by_transferred_to_medicine_advance ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>To Medcine. Adv.:</span><span style="color: #28a745;">₹${this.formatAmount(this.cashData.loan_received_by_transferred_to_medicine_advance)}</span></div>` : ''}
          ${this.cashData.loans_taken ? `<div style="font-size: 8px; display: flex; justify-content: space-between; margin: 0.5px 0; line-height: 1; padding: 1px 2px; background-color: #f8f9fa; border-radius: 2px;"><span>Taken:</span><span style="color: #dc3545;">₹${this.formatAmount(this.cashData.loans_taken)}</span></div>` : ''}
        </div>

        <!-- Expenses -->
        <div style="border: 1px solid #dee2e6; padding: 2px 4px; border-radius: 4px;display: flex;justify-content: space-between;">
          <div style="font-size: 10px; color: #6c757d; margin-bottom: 1px; font-weight: bold;">Expenses</div>
          <div style="font-size: 10px; font-weight: bold; color: #dc3545;">₹${this.formatAmount(this.cashData.expenses)}</div>
        </div>
      </div>
    </div>
  </div>
`
          }

          // Create iframe for printing
          const iframe = document.createElement('iframe')
          iframe.style.position = 'absolute'
          iframe.style.top = '-9999px'
          iframe.style.left = '-9999px'
          document.body.appendChild(iframe)

          // Write content to iframe
          iframe.contentDocument.open()
          iframe.contentDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              body {
                margin: 15px;
                font-family: Arial, sans-serif;
              }
              
              table {
                page-break-inside: auto;
              }
              
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              
              th, td {
                page-break-inside: avoid;
              }
            }
            
            @page {
              margin: 0.5in;
              size: A4 landscape;
            }
            
            body {
              font-family: Arial, sans-serif;
              margin: 15px 5px;
            }
          </style>
        </head>
        <body>
          ${summaryHTML}
          ${tableHTML}
          ${cashSummaryHTML}
        </body>
        </html>
      `)
          iframe.contentDocument.close()

          setTimeout(() => {
            iframe.contentWindow.print()

            iframe.contentWindow.addEventListener('afterprint', () => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
              }
              resolve('Print completed successfully')
            })

            // Fallback cleanup
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
                resolve('Print completed')
              }
            }, 500)
          }, 500)
        } catch (error) {
          reject(error)
        }
      })
    },
  },
}
</script>

<style scoped>
.compact-table {
  font-size: 0.875rem;
}

.compact-table th,
.compact-table td {
  padding: 0.25rem 0.5rem;
  vertical-align: middle;
}

.compact-table th {
  font-weight: 600;
}

/* Fixed width columns for Party/Remarks and Expense Remark */
.compact-table .fixed-width-column {
  max-width: 150px;
  min-width: 150px;
  width: 150px;
  word-wrap: break-word;
  white-space: normal;
}

.table-footer-highlight {
  background-color: #ffc107 !important;
}

.table-footer-highlight td {
  background-color: #ffc107 !important;
  font-weight: bold;
}

/* Cash Summary Styles */
.cash-summary-section {
  margin-top: 0.5rem;
}

.cash-summary-container {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.cash-summary-header {
  background-color: #007bff;
  color: white;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.cash-summary-body {
  padding: 0.5rem;
}

.cash-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  margin-bottom: 0.5rem;
}

.cash-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
}

.cash-value-large {
  font-size: 0.875rem;
  font-weight: 700;
}

.cash-details-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.cash-detail-column {
  flex: 1;
  min-width: 200px;
}

.cash-detail-box {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  display: flex;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  justify-content: space-between;
  color: #212529;
  line-height: 1.2;
  flex: 1;
  min-width: 150px;
}

.section-header-positive {
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.section-header-negative {
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.section-header-loan {
  background-color: #fff3cd;
  border-color: #ffeaa7;
}

.cash-detail-sub-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.15rem 0.5rem;
  margin-top: 0.15rem;
  background-color: #f8f9fa;
  border-radius: 3px;
  font-size: 0.875rem;
  line-height: 1.2;
}

.cash-detail-sub-box span:first-child {
  color: #6c757d;
}

.cash-detail-sub-box span:last-child {
  font-weight: 600;
}

@media print {
  .modal-footer {
    display: none !important;
  }
}
</style>
